import React from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/core';
import QButton from 'components/QButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import DialogContent from '@material-ui/core/DialogContent';
import CloseIcon from '@material-ui/icons/Clear';
import QPureComponent from 'QPureComponent';

import SearchBox from 'components/SearchBox';
import ScheduledList from './ScheduledList';

const styles = (theme) => ({
  dialogRoot: {
    backgroundColor: theme.palette.greyScale[7],
  },
  title: {
    paddingBottom: 8,
  },
  titleSpacing: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  closeButton: {
    marginTop: -14,
    marginRight: -12,
    marginBottom: 2,
    padding: 10,
    float: 'right',
  },
  searchBar: {
    paddingLeft: 4,
    borderRadius: 4,
    backgroundColor: theme.palette.greyScale[6],
    '&.open': {
      width: 'calc(100% - 18px)',
    },
  },
  searchIcon: {
    height: 18,
    color: theme.palette.greyScale[2],
  },
  searchInput: {
    fontSize: '14px',
    lineHeight: 1.43,
    fontWeight: 'normal',
    color: theme.palette.greyScale[0],
  },
  greyGrid: {
    padding: '0px 14px',
  },
  buttonBottoms: {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingBottom: 14,
    paddingLeft: 24,
    paddingRight: 24,
  },
  cap: {
    paddingTop: 4,
  },
});

export const ScheduledDialogType = Object.freeze({
  BILL: 'BILL',
  INCOME: 'INCOME',
  BOTH: 'BOTH',
});

class ScheduledDialog extends QPureComponent {
  constructor(props) {
    super(props);

    this.state = {
      search: null,
      selectedTxn: null,
    };
  }

  resetFunc = () => {
    this.setState({search: null});
  };

  closeModal = () => {
    this.setState({ selectedTxn: undefined, search: undefined });
    this.props.closeFunc();
  };

  selectTxn = (event) => {
    const selectTxn = JSON.parse(event.currentTarget.id);
    const currentTxn = this.state.selectedTxn;
    if (currentTxn && (currentTxn.id === selectTxn.id)) {
      this.setState({ selectedTxn: null });
    } else {
      this.setState({ selectedTxn: selectTxn });
    }
  };

  clickAdd = () => {
    this.props.goFunc(this.state.selectedTxn);
    this.closeModal();
  };

  render() {
    super.render();
    const { classes, type, open, hideBackdrop } = this.props;
    let title;
    let subTitle;
    switch (type) {
      case ScheduledDialogType.BILL:
        title = 'Select Bills';
        subTitle = 'Select only if the items are recurring bills or subscriptions';
        break;
      case ScheduledDialogType.INCOME:
        title = 'Select Income';
        subTitle = 'Select only if the items are recurring sources of income';
        break;
      case ScheduledDialogType.BOTH:
      default:
        title = 'Add a Bill or Paycheck';
        subTitle = 'Select from one of your transactions below.';
    }
    return (
      <ErrorBoundary>
        <Dialog
          title={title}
          open={open}
          fullWidth
          maxWidth="md"
          classes={{
            paper: classes.dialogRoot,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              this.closeModal();
            }
          }}
          onBackdropClick={this.closeModal}
          hideBackdrop={hideBackdrop}
        >
          <DialogTitle id="simple-dialog-title" classes={{ root: classes.title}}>
            <div className={classes.titleSpacing}>
              <ListItemText
                primary={title}
                primaryTypographyProps={{ variant: 'h5' }}
                secondary={subTitle}
                secondaryTypographyProps={{ variant: 'subtitle1' }}
                classes={{ secondary: classes.cap, primary: classes.cap }}
              />
              <div>
                <IconButton
                  className={classes.closeButton}
                  aria-label="Close"
                  id="dlg-close"
                  onClick={this.closeModal}
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          </DialogTitle>
          <DialogContent className={classes.greyGrid}>
            <SearchBox
              onSearch={(search) => {
                this.setState({ search })
              }}
              autoSearch={false}
              collapsable={false}
              initialValue={this.state.search}
              placeholder="Search Transactions"
              classes={{
                root: classes.searchBar,
                input: classes.searchInput,
              }}
            />
            <ScheduledList
              clickFunc={this.selectTxn}
              highlight={this.state.selectedTxn && this.state.selectedTxn.id}
              filter={this.state.search}
              onReset={this.resetFunc}
              isBill={type === ScheduledDialogType.BOTH ? undefined : type === ScheduledDialogType.BILL}
            />
          </DialogContent>
          <div className={classes.buttonBottoms}>
            <QButton
              variant="contained"
              disabled={!this.state.selectedTxn}
              onClick={this.clickAdd}
            >
              {type === ScheduledDialogType.BOTH ? 'ADD' : 'ADD TO LIST'}
            </QButton>
          </div>
        </Dialog>
      </ErrorBoundary>
    );
  };
}

ScheduledDialog.defaultProps = {
  open: false,
  type: ScheduledDialogType.BOTH,
  hideBackdrop: false,
  multiSelect: false,
};

ScheduledDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool,
  closeFunc: PropTypes.func,
  goFunc: PropTypes.func,
  type: PropTypes.string,
  hideBackdrop: PropTypes.bool,
  multiSelect: PropTypes.bool,
};

export default compose(
  withStyles(styles),
)(ScheduledDialog);
